import { useEffect, useState } from "react";
import type { WorkItem } from "@/utils/videoUtils";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  work: WorkItem;
}

const CHANNELS = [
  { key: "wechat", label: "微信", icon: "ri-wechat-fill", cls: "bg-secondary-100 text-secondary-900" },
  { key: "weibo", label: "微博", icon: "ri-weibo-fill", cls: "bg-accent-100 text-accent-900" },
  { key: "x", label: "X", icon: "ri-twitter-x-fill", cls: "bg-background-200 text-foreground-800" },
  { key: "link", label: "复制链接", icon: "ri-link", cls: "bg-primary-100 text-primary-800" },
];

export default function ShareModal({ open, onClose, work }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const pageUrl = `${window.location.origin}${window.location.pathname}#/detail/${work.id}`;

  const handleChannel = async (key: string) => {
    if (key === "link") {
      try {
        await navigator.clipboard.writeText(pageUrl);
      } catch {
        // clipboard unavailable in demo environment
      }
      setCopied(true);
    } else {
      setCopied(true);
    }
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-primary-950/50" onClick={onClose}></div>
      <div className="relative z-10 w-full max-w-md rounded-lg bg-background-50 border border-background-200/70 p-6 animate-soft-in">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">分享作品</h3>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <p className="text-sm text-foreground-600 mb-5 line-clamp-1">{work.title} · {work.year}</p>

        <div className="grid grid-cols-4 gap-3">
          {CHANNELS.map((ch) => (
            <button
              key={ch.key}
              type="button"
              onClick={() => handleChannel(ch.key)}
              className="flex flex-col items-center gap-2 cursor-pointer group"
            >
              <span className={`w-12 h-12 flex items-center justify-center rounded-lg ${ch.cls} transition-transform group-hover:scale-105`}>
                <i className={`${ch.icon} text-xl`}></i>
              </span>
              <span className="text-xs font-label text-foreground-700">{ch.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-md bg-background-100 border border-background-200/70 px-3 py-2 text-xs text-foreground-600 font-label break-all">
          {pageUrl}
        </div>

        {copied && (
          <p className="mt-3 text-center text-xs font-label text-accent-700">
            链接已复制（演示功能）
          </p>
        )}
        <p className="mt-4 text-center text-xs font-label text-foreground-500">
          分享为前端演示效果，未接入真实分享接口
        </p>
      </div>
    </div>
  );
}